import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Clock, ArrowRight, Download, BookOpen, RefreshCw, Mail } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_TRIES = 24;        // ~60s of resilient polling (24 × 2.5s)
const POLL_INTERVAL = 2500;

// Post-purchase cross-sell — no cart needed; one focused, relevant next step.
const NEXT_STEP = {
  template: { title: "Make it official", desc: "Turn your new website into a fundable business with a professional business plan.", to: "/business-plan", cta: "Build my business plan" },
  plan_unlock: { title: "Bring your plan to life", desc: "Ready for a professional website that matches your new plan? Let's build it.", to: "/services", cta: "Explore website packages" },
  concierge_bundle: { title: "Get funding-ready", desc: "Pair your AI Concierge with a professional business plan to unlock funding.", to: "/business-plan", cta: "Build my business plan" },
  deposit: { title: "Get funding-ready", desc: "Add a professional business plan to strengthen your launch and funding options.", to: "/business-plan", cta: "Build my business plan" },
  balance: { title: "Grow with an AI Concierge", desc: "Keep leads flowing after launch with an always-on AI assistant on your site.", to: "/ai-concierge", cta: "See the AI Concierge" },
  invoice: { title: "Keep the momentum", desc: "Explore more ways to grow — from a business plan to an AI concierge.", to: "/services", cta: "See what's next" },
};

function NextStep({ kind }) {
  const s = NEXT_STEP[kind] || { title: "Keep building", desc: "Explore more ways to plan, launch and grow your business.", to: "/services", cta: "Explore services" };
  return (
    <div className="mt-8 border border-gold/25 bg-gold/[0.06] p-5" data-testid="checkout-next-step">
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold mb-1.5">Recommended next step</div>
      <h3 className="font-heading text-lg text-white mb-1">{s.title}</h3>
      <p className="text-sm text-gray-300 mb-4">{s.desc}</p>
      <Link to={s.to} data-testid="next-step-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors">
        {s.cta} <ArrowRight size={15} />
      </Link>
    </div>
  );
}

export default function CheckoutStatus({ canceled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(canceled ? "canceled" : "checking");
  const [info, setInfo] = useState(null);
  const [pollKey, setPollKey] = useState(0);
  const [rechecking, setRechecking] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (canceled || !sessionId) return;
    let stop = false;
    attempts.current = 0;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= MAX_TRIES) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, canceled, pollKey]);

  // Manual re-check for the timeout fallback — a single fetch that resumes auto-polling
  // if the payment still isn't confirmed, so the buyer is never truly stuck.
  const recheck = useCallback(async () => {
    if (!sessionId || rechecking) return;
    setRechecking(true);
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") { setState("success"); return; }
      if (data.status === "expired") { setState("expired"); return; }
      setState("checking");
      setPollKey((k) => k + 1); // restart the resilient polling loop
    } catch {
      // keep the reassuring fallback visible; let them try again
    } finally {
      setRechecking(false);
    }
  }, [sessionId, rechecking]);

  // SUCCESS — onboarding into the ATF WebWorks ecosystem (not a receipt).
  if (state === "success") {
    // Post-purchase integrity guard: if the order failed verification, never present a
    // successful download — reassure the customer and confirm support was notified.
    if (info && info.integrity_ok === false) {
      return (
        <div className="max-w-xl mx-auto px-6 py-24 md:py-28 text-center" data-testid="checkout-preparing">
          <div className="w-20 h-20 mx-auto glass flex items-center justify-center mb-8">
            <Clock className="text-gold" size={44} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-white" data-testid="preparing-title">
            We're preparing your order
          </h1>
          <p className="text-gray-300 font-light mt-5 leading-relaxed">
            Thank you — your payment was received successfully. We're putting the final touches on your
            files to make sure everything is perfect before delivery.
          </p>
          <p className="text-sm text-gray-400 mt-4 leading-relaxed">
            Our team has been notified automatically and will have your website ready in your portal shortly.
            If you have any questions, reach us at{" "}
            <a href="mailto:support@asktopfoster.com" className="text-gold hover:underline">support@asktopfoster.com</a>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
            <Link to="/portal" data-testid="preparing-portal-btn"
              className="inline-flex items-center justify-center bg-gold text-navy px-8 py-3.5 font-semibold hover:bg-gold-light transition-colors">
              Go to My Portal
            </Link>
            <Link to="/marketplace" data-testid="preparing-browse-btn"
              className="inline-flex items-center justify-center border border-white/25 text-white px-8 py-3.5 hover:bg-white/5 transition-colors">
              Browse More Templates
            </Link>
          </div>
        </div>
      );
    }
    // TEMPLATE BUYERS — frictionless, BootstrapMade-style instant download. No portal.
    const isTemplate = info && info.kind === "template" && info.download_available;
    if (isTemplate) {
      const dl = `${BACKEND}${info.download_url}`;
      const guide = info.guide_url ? `${BACKEND}${info.guide_url}` : null;
      const acctUrl = info.buyer_email ? `/register?email=${encodeURIComponent(info.buyer_email)}` : "/register";
      return (
        <div className="max-w-xl mx-auto px-6 py-24 md:py-28" data-testid="checkout-success">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto glass flex items-center justify-center mb-8">
              <CheckCircle2 className="text-green-400" size={48} />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-white leading-[1.05]" data-testid="success-title">
              Thank you for your purchase!
            </h1>
            <p className="text-lg text-gray-300 font-light mt-5" data-testid="success-subtitle">
              Your template is ready.
            </p>
          </div>

          {info && (
            <div className="glass p-5 mt-8 flex justify-between items-center" data-testid="success-order-summary">
              <span className="text-gray-300">{info.template_title || info.description || "Your template"}</span>
              <span className="text-white font-semibold">{money((info.amount_total || 0) / 100)}</span>
            </div>
          )}

          {/* PRIMARY — instant download, no login required */}
          <a href={dl} data-testid="success-download-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-lg font-semibold hover:bg-gold-light transition-colors">
            <Download size={20} /> Download Your Template
          </a>
          {guide && (
            <a href={guide} data-testid="success-guide-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/25 text-white px-8 py-3.5 hover:bg-white/5 transition-colors">
              <BookOpen size={18} /> Download Installation Guide
            </a>
          )}

          {info.license_key && (
            <div className="mt-6 glass p-4 flex items-center justify-between gap-3" data-testid="success-license">
              <span className="text-xs uppercase tracking-wider text-gray-500">License key</span>
              <code className="text-gold text-sm tracking-wide">{info.license_key}</code>
            </div>
          )}

          {/* Reassurance — the customer is done and can safely leave */}
          <p className="text-sm text-gray-400 mt-4 leading-relaxed text-center" data-testid="success-reassurance">
            Your download has started successfully. You may safely close this page after the download
            completes. We've also emailed your secure download link for your convenience.
          </p>

          {/* PRIMARY OPTIONAL ACTION — keep shopping */}
          <Link to="/marketplace" data-testid="success-browse-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 border border-gold/60 text-gold px-8 py-3.5 text-base font-semibold hover:bg-gold/10 transition-colors">
            Continue Shopping <ArrowRight size={18} />
          </Link>

          <NextStep kind={info.kind} />

          {/* SECONDARY — support + custom project path */}
          <div className="mt-6 flex flex-col items-center gap-2.5 text-sm">
            <a href="mailto:support@asktopfoster.com" data-testid="success-support-btn" className="text-gray-400 hover:text-gold transition-colors">Need help? Contact Support</a>
            <Link to="/start" data-testid="success-custom-btn" className="text-gray-500 hover:text-gold transition-colors">Need a custom website? Start a Custom Project →</Link>
          </div>

          {/* OPTIONAL, DE-EMPHASIZED — free account nudge (never required) */}
          <div className="mt-8 border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="success-account-nudge">
            <p className="text-xs text-gray-500 leading-relaxed">
              Create a free ATF WebWorks account to re-download purchases anytime, receive updates, and keep your purchases organized.
            </p>
            <Link to={acctUrl} data-testid="success-create-account-btn"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-300 hover:text-gold transition-colors whitespace-nowrap">
              Create Free Account <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      );
    }

    // CUSTOM PROJECT PAYMENTS (deposit/balance/invoice) — the portal is their home.
    return (
      <div className="max-w-xl mx-auto px-6 py-24 md:py-28" data-testid="checkout-success">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto glass flex items-center justify-center mb-8">
            <CheckCircle2 className="text-green-400" size={48} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-white leading-[1.05]" data-testid="success-title">
            Payment received!
          </h1>
          <p className="text-lg text-gray-300 font-light mt-5" data-testid="success-subtitle">
            Thank you — your project workspace is ready.
          </p>
        </div>
        {info && (
          <div className="glass p-5 mt-8 flex justify-between items-center" data-testid="success-order-summary">
            <span className="text-gray-300">{info.description || "Payment"}</span>
            <span className="text-white font-semibold">{money((info.amount_total || 0) / 100)}</span>
          </div>
        )}
        <Link to="/portal" data-testid="success-primary-btn"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-lg font-semibold hover:bg-gold-light transition-colors">
          Go to Your Project Workspace <ArrowRight size={20} />
        </Link>
        <NextStep kind={info?.kind} />
      </div>
    );
  }

  // TIMEOUT — reassuring fallback so a buyer is NEVER left stuck on a spinner.
  // The payment almost certainly succeeded; verification (Stripe webhook) is just lagging.
  if (state === "timeout") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 md:py-28 text-center" data-testid="checkout-timeout">
        <div className="w-20 h-20 mx-auto glass flex items-center justify-center mb-8">
          <Mail className="text-gold" size={40} />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-white" data-testid="timeout-title">
          Payment received — finalizing your order
        </h1>
        <p className="text-gray-300 font-light mt-5 leading-relaxed" data-testid="timeout-message">
          Thanks! Your payment went through. Verification is taking a little longer than usual on our
          side. We've emailed your secure download link — it should arrive within a few minutes — and
          it will appear here the moment verification completes.
        </p>
        <p className="text-sm text-gray-400 mt-4 leading-relaxed">
          No need to pay again. If you don't see the email shortly, check your spam folder or reach us at{" "}
          <a href="mailto:support@asktopfoster.com" className="text-gold hover:underline">support@asktopfoster.com</a>.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
          <button onClick={recheck} disabled={rechecking} data-testid="timeout-recheck-btn"
            className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-3.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
            <RefreshCw size={18} className={rechecking ? "animate-spin" : ""} />
            {rechecking ? "Checking…" : "Check again"}
          </button>
          <a href="mailto:support@asktopfoster.com" data-testid="timeout-support-btn"
            className="inline-flex items-center justify-center border border-white/25 text-white px-8 py-3.5 hover:bg-white/5 transition-colors">
            Contact Support
          </a>
          <Link to="/marketplace" data-testid="timeout-browse-btn"
            className="inline-flex items-center justify-center text-gray-400 px-6 py-3.5 hover:text-gold transition-colors">
            See our work
          </Link>
        </div>
      </div>
    );
  }

  // Non-success states keep the simple centered layout.
  const configs = {
    checking: { icon: <Loader2 className="animate-spin text-gold" size={40} />, title: "Confirming your payment…", desc: "Please wait a moment while we verify with Stripe." },
    canceled: { icon: <XCircle className="text-gray-400" size={48} />, title: "Checkout canceled", desc: "No charge was made. You can try again anytime." },
    expired: { icon: <Clock className="text-gold" size={48} />, title: "Session expired", desc: "Your checkout session expired. Please start again." },
  };
  const c = configs[state] || configs.checking;

  return (
    <div className="max-w-lg mx-auto px-6 py-32 text-center" data-testid="checkout-status">
      <div className="w-20 h-20 mx-auto glass flex items-center justify-center mb-8">{c.icon}</div>
      <h1 className="font-heading text-3xl text-white mb-3" data-testid="checkout-status-title">{c.title}</h1>
      <p className="text-gray-400 mb-6">{c.desc}</p>
      <div className="flex gap-4 justify-center">
        <Link to="/marketplace" className="border border-white/25 text-white px-8 py-3.5 hover:bg-white/5 transition-colors">Our Work</Link>
      </div>
    </div>
  );
}
